import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { CheckCircle2, Lock } from 'lucide-react';
import { Card, Button } from '../components';
import { getThermometer, vulThermometerIn } from '../services/medewerkerApi';
import type { ThermometerPijlerKey } from '../services/medewerkerApi';
import { MedewerkerPeilingen } from '../components/MedewerkerPeilingen';

type Scores = Record<ThermometerPijlerKey, number>;

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

/** Pijler 3: anonieme Bedrijfsthermometer (4 pijlers, score 1–5). */
const ThermometerBlok: React.FC = () => {
  const { t } = useTranslation('medewerker');
  const queryClient = useQueryClient();
  const [scores, setScores] = useState<Partial<Scores>>({});
  const [verzonden, setVerzonden] = useState<string | null>(null);

  const thermoQuery = useQuery({ queryKey: ['medewerker-thermometer'], queryFn: getThermometer });

  // Standaard op 3 (neutraal) per pijler zodra de ronde geladen is.
  useEffect(() => {
    if (!thermoQuery.data) return;
    setScores((prev) => {
      const next: Partial<Scores> = { ...prev };
      for (const p of thermoQuery.data.pijlers) if (next[p.key] === undefined) next[p.key] = 3;
      return next;
    });
  }, [thermoQuery.data]);

  const vulMutation = useMutation({
    mutationFn: (body: Scores) => vulThermometerIn(body),
    onSuccess: (res) => {
      setVerzonden(res.periode_label);
      queryClient.invalidateQueries({ queryKey: ['medewerker-thermometer'] });
      queryClient.invalidateQueries({ queryKey: ['medewerker-dashboard'] });
    },
  });

  if (thermoQuery.isLoading) {
    return <div className="py-16 text-center text-gray-500">…</div>;
  }

  if (thermoQuery.isError || !thermoQuery.data) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-red-600" role="alert">
          {foutMelding(thermoQuery.error, t('thermometer.laadfout', { defaultValue: 'De thermometer kon niet worden geladen.' }))}
        </p>
        <Button variant="outline" className="mt-4" onClick={() => thermoQuery.refetch()}>
          {t('algemeen.opnieuw', { defaultValue: 'Opnieuw proberen' })}
        </Button>
      </Card>
    );
  }

  const ronde = thermoQuery.data;

  if (verzonden) {
    return (
      <Card className="p-8 text-center">
        <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" aria-hidden="true" />
        <h1 className="text-xl font-semibold text-gray-900">{t('thermometer.dank', { defaultValue: 'Dank je wel' })}</h1>
        <p className="text-gray-700 mt-2">
          {t('thermometer.dankUitleg', { defaultValue: 'Je antwoorden voor {{periode}} zijn anoniem opgeslagen.', periode: verzonden })}
        </p>
        <Link to="/medewerker" className="inline-block mt-6">
          <Button variant="outline">{t('vp.naarDashboard', { defaultValue: 'Naar mijn overzicht' })}</Button>
        </Link>
      </Card>
    );
  }

  if (ronde.ingevuld) {
    return (
      <div className="space-y-5">
        <h1 className="text-2xl font-bold text-gray-900">{t('pijler.thermometer', { defaultValue: 'Bedrijfsthermometer' })}</h1>
        <Card className="p-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" aria-hidden="true" />
          <p className="text-gray-900 font-medium">
            {t('thermometer.alIngevuld', { defaultValue: 'Je hebt de thermometer voor {{periode}} al ingevuld.', periode: ronde.periode_label })}
          </p>
          <p className="text-sm text-gray-600 mt-2">
            {t('thermometer.alIngevuldUitleg', { defaultValue: 'Bedankt! Bij de volgende ronde staat hij weer voor je klaar.' })}
          </p>
          <Link to="/medewerker" className="inline-block mt-6">
            <Button variant="outline">{t('vp.naarDashboard', { defaultValue: 'Naar mijn overzicht' })}</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const compleet = ronde.pijlers.every((p) => scores[p.key] !== undefined);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!compleet) return;
    vulMutation.mutate(scores as Scores);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('pijler.thermometer', { defaultValue: 'Bedrijfsthermometer' })}</h1>
        <p className="text-sm text-gray-600 mt-0.5">
          {ronde.periode_label} · {t('thermometer.duur', { defaultValue: '2 minuten' })}
        </p>
      </div>

      <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-[#0B1F3A] shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-gray-800">
          <strong className="text-gray-900">{t('thermometer.privacyTitel', { defaultValue: '100% anoniem.' })}</strong>{' '}
          {t('thermometer.privacy', {
            defaultValue:
              'Je antwoorden worden opgeslagen zonder enige koppeling aan jouw account, telefoonnummer of code. Je werkgever ziet alleen gemiddelden van groepen van minimaal 10 medewerkers.',
          })}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {ronde.pijlers.map((p, index) => {
          const waarde = scores[p.key] ?? 3;
          const inputId = `pijler-${p.key}`;
          return (
            <Card key={p.key} className="p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">
                {index + 1}/{ronde.pijlers.length} · {p.label}
              </p>
              <label htmlFor={inputId} className="block font-medium text-gray-900 mt-1">
                {p.vraag}
              </label>
              <div className="text-center my-3">
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#0B1F3A] text-white text-2xl font-bold" aria-hidden="true">
                  {waarde}
                </span>
              </div>
              <input
                id={inputId}
                type="range"
                min={1}
                max={5}
                step={1}
                value={waarde}
                onChange={(e) => setScores((prev) => ({ ...prev, [p.key]: Number(e.target.value) }))}
                className="w-full accent-[#0B1F3A] h-2 cursor-pointer"
                aria-valuemin={1}
                aria-valuemax={5}
                aria-valuenow={waarde}
                aria-valuetext={`${waarde} van 5`}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1.5">
                <span className="max-w-[45%]">{p.laag}</span>
                <span className="max-w-[45%] text-right">{p.hoog}</span>
              </div>
            </Card>
          );
        })}

        {vulMutation.isError && (
          <p className="text-sm text-red-600" role="alert">
            {foutMelding(vulMutation.error, t('thermometer.mislukt', { defaultValue: 'Versturen is niet gelukt. Probeer het opnieuw.' }))}
          </p>
        )}

        <Button type="submit" size="lg" className="w-full" disabled={!compleet || vulMutation.isPending} loading={vulMutation.isPending}>
          {t('thermometer.versturen', { defaultValue: 'Anoniem versturen' })}
        </Button>
      </form>
    </div>
  );
};

/** Lopende pulse-peilingen van de werkgever (bovenaan) + de vaste thermometer. */
export const MedewerkerThermometerPage: React.FC = () => (
  <div className="space-y-8">
    <MedewerkerPeilingen />
    <ThermometerBlok />
  </div>
);

export default MedewerkerThermometerPage;
